'use client'

import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useTranslations } from '@/components/I18nProvider'
import { Star, Quote, ChevronLeft, ChevronRight } from 'lucide-react'
import { Button } from '@sarkiai/ui'

interface Testimonial {
  name: string
  role: string
  content: string
}

// Mock data for testing - will be overridden by translations if available
const mockTestimonials: Testimonial[] = [
  {
    name: "STD SA",
    role: "Société de Transit et de Dédouanement",
    content: "L'intégration de l'Agent IA de SarkiAI a révolutionné notre gestion du fret. Nous avons réduit nos délais de traitement de 40% tout en améliorant la précision de notre suivi."
  },
  {
    name: "PefI SA",
    role: "Direction des Opérations",
    content: "La solution VCard de Djotta a transformé notre manière d'échanger nos contacts. C'est rapide, professionnel et parfaitement adapté à nos besoins logistiques."
  },
  {
    name: "Picardi Mall",
    role: "Service Client",
    content: "Le passage aux cartes VCard a modernisé l'accueil de nos clients. Un simple geste suffit pour partager nos informations, ce qui renforce notre image innovante."
  },
]

export function TestimonialsSection() {
  const t = useTranslations('enterprise_page.testimonials')
  const [activeIndex, setActiveIndex] = useState(0)

  // @ts-ignore - Use mock data as fallback
  const rawItems = t.raw('items')
  const items: Testimonial[] = Array.isArray(rawItems) && rawItems.length > 0 ? rawItems : mockTestimonials

  const next = () => setActiveIndex((prev) => (prev + 1) % items.length)
  const prev = () => setActiveIndex((prev) => (prev - 1 + items.length) % items.length)

  // Auto-scroll
  useEffect(() => {
    const timer = setInterval(next, 8000)
    return () => clearInterval(timer)
  }, [items.length])

  return (
    <section className="py-24 relative px-4 lg:px-12">
      <div className="container mx-auto">
        <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-3xl md:text-5xl uppercase tracking-tighter leading-none text-foreground"
          >
            {t('sectionTitle')}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-lg text-foreground/60 leading-relaxed"
          >
            {t('sectionSubtitle')}
          </motion.p>
        </div>

        <div className="w-full mx-auto relative px-2 sm:px-12">
          {/* Testimonials Carousel */}
          <div className="relative min-h-[420px] sm:min-h-[300px] overflow-hidden">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeIndex}
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ duration: 0.5, ease: "easeInOut" }}
                className="absolute inset-0 flex items-center justify-center"
              >
                <div className="w-full p-8 md:p-12 rounded-[2rem] border border-white/10 bg-white/5 backdrop-blur-xl relative">
                  <Quote className="absolute top-6 right-6 w-12 h-12 text-foreground/5" strokeWidth={1} />

                  <div className="space-y-6 text-center">
                    <div className="flex justify-center gap-1">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 fill-white text-foreground" />
                      ))}
                    </div>

                    <p className="text-xl md:text-2xl font-medium leading-relaxed italic text-foreground max-w-2xl mx-auto">
                      "{items[activeIndex].content}"
                    </p>

                    <div className="p-4 rounded-3xl border-t border-white/10 inline-block bg-black/5">
                      <div className="flex items-center justify-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center text-xs uppercase text-foreground">
                          {items[activeIndex].name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                        </div>
                        <div className="text-left">
                          <h4 className="text-lg uppercase tracking-tight text-foreground">{items[activeIndex].name}</h4>
                          <p className="text-xs text-foreground/60 uppercase tracking-widest">{items[activeIndex].role}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Navigation Controls */}
          <div className="absolute top-1/2 -left-4 -translate-y-1/2 z-20">
            <Button
              variant="outline"
              size="icon"
              onClick={prev}
              className="w-12 h-12 rounded-full border-black/10 dark:border-white/10 bg-background hover:bg-black/5 dark:hover:bg-white/5 shadow-lg transition-all"
            >
              <ChevronLeft className="w-6 h-6" />
            </Button>
          </div>

          <div className="absolute top-1/2 -right-4 -translate-y-1/2 z-20">
            <Button
              variant="outline"
              size="icon"
              onClick={next}
              className="w-12 h-12 rounded-full border-black/10 dark:border-white/10 bg-background hover:bg-black/5 dark:hover:bg-white/5 shadow-lg transition-all"
            >
              <ChevronRight className="w-6 h-6" />
            </Button>
          </div>

          {/* Pagination dots */}
          <div className="flex justify-center gap-2 mt-8">
            {items.map((_, i) => (
              <button
                key={i}
                onClick={() => setActiveIndex(i)}
                className={`h-1.5 transition-all duration-300 rounded-full ${activeIndex === i ? 'w-8 bg-foreground' : 'w-2 bg-foreground/20 hover:bg-foreground/40'
                  }`}
                aria-label={`Go to testimonial ${i + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}
